import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Camera, Monitor, Plus, Check, Handshake, Trophy, AlertTriangle, Gavel, Loader2,
} from 'lucide-react';
import { pmApi, type WeeklyReviewData, type Commitment } from './pmApi';
import {
  StateLabel, RiskLevel, ReasonList, Stat, EmptyState, LoadingBlock,
  fmtDate, fmtDateFull, cleanName, cleanBlocker,
} from './components';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/shadcn/ui/card';
import { Button } from '../../../components/shadcn/ui/button';
import { Badge } from '../../../components/shadcn/ui/badge';
import { Input } from '../../../components/shadcn/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/shadcn/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/shadcn/ui/select';

/* The Weekly Business Review.

   Success is narrow and concrete: leadership runs the meeting from this page
   without opening the spreadsheet. It is ordered the way the meeting runs —
   where we stand, what we committed to and whether we delivered, what we
   finished, what broke, what needs a decision.

   Commitments sit above wins deliberately. */

function CommitmentRow({ c, onChange }: { c: Commitment; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const mark = async (status: string) => {
    setBusy(true);
    try { await pmApi.updateCommitment(c.id, { status }); onChange(); } finally { setBusy(false); }
  };
  return (
    <TableRow>
      <TableCell className="font-medium">{c.commitment}</TableCell>
      <TableCell className="text-sm">{c.owner_role || '—'}</TableCell>
      <TableCell className="text-sm tabular-nums">{fmtDate(c.due_date)}</TableCell>
      <TableCell>
        <Badge variant={c.status === 'MISSED' ? 'destructive' : c.status === 'FULFILLED' ? 'secondary' : 'outline'}>
          {c.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {c.status === 'OPEN' ? (
          <div className="flex justify-end gap-1">
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => mark('FULFILLED')}>
              <Check className="h-3 w-3" /> Fulfilled
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={busy} onClick={() => mark('MISSED')}>
              Missed
            </Button>
          </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function AddCommitment({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [owners, setOwners] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    pmApi.getOwners().then((r) => { if (r.success && r.data) setOwners(r.data.map((o) => o.role_code)); });
  }, []);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await pmApi.createCommitment({
        commitment: text.trim(),
        ownerRole: owner || undefined,
        dueDate: due || undefined,
      });
      setText(''); setOwner(''); setDue(''); setOpen(false);
      onAdded();
    } finally { setSaving(false); }
  };

  if (!open) {
    return <Button size="sm" onClick={() => setOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> Record a commitment</Button>;
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <Input
        autoFocus
        className="min-w-[240px] flex-1"
        placeholder="e.g. Controller will finalize accounting platform configuration"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(false); }}
      />
      <Select value={owner} onValueChange={setOwner}>
        <SelectTrigger className="w-32"><SelectValue placeholder="Owner" /></SelectTrigger>
        <SelectContent>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" className="w-40" value={due} onChange={(e) => setDue(e.target.value)} />
      <Button size="sm" onClick={submit} disabled={saving || !text.trim()} className="gap-2">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
}

export default function WeeklyReview() {
  const navigate = useNavigate();
  const [data, setData] = useState<WeeklyReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [present, setPresent] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pmApi.getWeeklyReview();
      if (res.success && res.data) setData(res.data);
      else setError(res.error || 'Could not generate the review.');
    } catch {
      setError('Could not reach the command center service.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const takeSnapshot = async () => {
    const res = await pmApi.snapshotWeeklyReview();
    if (res.success && res.data) {
      setSnapshotMsg(`Snapshot frozen for week ending ${fmtDateFull(res.data.weekEnding)}. Next week's review will compare against it.`);
      load();
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-display font-semibold text-[#242424] dark:text-foreground sm:text-2xl">Weekly Business Review</h1>
        <Card className="flex flex-col gap-6 shadow-none border-border"><CardContent className="p-0"><LoadingBlock label="Generating the review" /></CardContent></Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-display font-semibold text-[#242424] dark:text-foreground sm:text-2xl">Weekly Business Review</h1>
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
          {error}
        </p>
      </div>
    );
  }
  if (!data) return null;

  const s = data.executiveSummary;
  const ledger = [...data.missedCommitments, ...data.overdueOpenCommitments, ...data.nextWeekCommitments]
    .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i);

  return (
    <div className={`min-w-0 space-y-5 sm:space-y-6 ${present ? 'text-[17px]' : ''}`}>
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/command')} className="-ml-2 gap-1">
        <ArrowLeft className="h-4 w-4" /> Operational Command
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className={`font-display font-semibold text-[#242424] dark:text-foreground ${present ? 'text-3xl' : 'text-xl sm:text-2xl'}`}>
            Weekly Business Review
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Week ending {fmtDateFull(data.weekEnding)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPresent((v) => !v)} className="gap-2">
            <Monitor className="h-4 w-4" /> {present ? 'Exit presentation' : 'Presentation mode'}
          </Button>
          <Button size="sm" onClick={takeSnapshot} className="gap-2"><Camera className="h-4 w-4" /> Freeze snapshot</Button>
        </div>
      </div>

      {snapshotMsg ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
          {snapshotMsg}
        </p>
      ) : null}

      <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        {data.isBaseline ? (
          <>
            <span className="font-medium text-foreground/80">This is the baseline review.</span> No prior snapshot
            exists, so &ldquo;what changed&rdquo; cannot be computed yet — everything below is current state rather than
            a delta. Freeze a snapshot at the end of this meeting and next week&rsquo;s review will compare against it.
          </>
        ) : (
          <>Compared against the snapshot frozen for week ending {fmtDateFull(data.comparedTo)}.</>
        )}
      </p>

      {/* Executive snapshot */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Critical path" value={<StateLabel state={s.headline} />} sub={s.terminalMilestone ? `${s.terminalMilestone.cleanName} · ${fmtDate(s.terminalMilestone.dueDate)}` : undefined} />
        <Stat label="Streams at risk" value={s.atRisk} sub={`of ${data.metrics.workstreams}`} tone={s.atRisk ? 'risk' : 'ok'} />
        <Stat label="Overdue critical" value={s.overdueCritical} tone={s.overdueCritical ? 'breach' : 'ok'} />
        <Stat label="Decisions required" value={s.decisionsRequired} tone={s.decisionsRequired ? 'warn' : 'ok'} />
        <Stat label="Commitments missed" value={data.metrics.commitmentsMissed} tone={data.metrics.commitmentsMissed ? 'breach' : 'ok'} />
        <Stat label="Milestones breached" value={data.metrics.milestonesBreached} tone={data.metrics.milestonesBreached ? 'breach' : 'ok'} />
      </div>

      {/* Commitments */}
      <Card className="flex flex-col gap-6 shadow-none border-border">
        <CardHeader className="gap-3 pb-3">
          <div>
            <CardTitle as="h2" className="text-base">Commitments</CardTitle>
            <p className="text-sm text-muted-foreground">What we said we would do, and whether we did it.</p>
          </div>
          <AddCommitment onAdded={load} />
        </CardHeader>
        <CardContent className="p-0">
          {ledger.length ? (
            <div className="overflow-x-auto">
              <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_th]:text-foreground">
                <TableHeader>
                  <TableRow>
                    <TableHead>Commitment</TableHead><TableHead className="w-24">Owner</TableHead>
                    <TableHead className="w-24">Due</TableHead><TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-40 text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>{ledger.map((c) => <CommitmentRow key={c.id} c={c} onChange={load} />)}</TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={<Handshake className="h-8 w-8" />} title="No commitments recorded yet">
              The master plan export contains none — this ledger starts when leadership records the
              first one. Use &ldquo;Record a commitment&rdquo; above during the meeting.
            </EmptyState>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-5 xl:grid-cols-2">
        {/* Wins */}
        <Card className="flex flex-col gap-6 shadow-none border-border">
          <CardHeader className="pb-3">
            <CardTitle as="h2" className="text-base">Wins</CardTitle>
            <p className="text-sm text-muted-foreground">{data.isBaseline ? 'Completed to date.' : 'Completed since the last review.'}</p>
          </CardHeader>
          <CardContent className="p-0">
            {data.wins.length ? (
              <div className="divide-y divide-border">
                {data.wins.map((w) => (
                  <div key={w.id} className="flex items-start gap-3 p-4">
                    <Trophy className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#242424] dark:text-foreground">{cleanName(w.name)}</p>
                      <p className="text-xs text-muted-foreground">{w.workstream} · {w.owner || '—'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="Nothing completed in this period" />}
          </CardContent>
        </Card>

        {/* Blockers */}
        <Card className="flex flex-col gap-6 shadow-none border-border">
          <CardHeader className="pb-3">
            <CardTitle as="h2" className="text-base">Blockers</CardTitle>
            <p className="text-sm text-muted-foreground">{data.isBaseline ? 'All open blockers.' : 'New since the last review.'}</p>
          </CardHeader>
          <CardContent className="p-0">
            {data.newBlockers.length ? (
              <div className="divide-y divide-border">
                {data.newBlockers.map((b) => (
                  <div key={b.id} className="flex items-start gap-3 p-4">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#242424] dark:text-foreground">{cleanName(b.name)}</p>
                      <p className="text-xs text-muted-foreground">{b.workstream} · {b.owner || 'No owner'}</p>
                      {b.reason ? <p className="mt-1 text-sm text-foreground/75">{cleanBlocker(b.reason)}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="No blockers" />}
          </CardContent>
        </Card>
      </div>

      {/* Critical path */}
      <Card className="flex flex-col gap-6 shadow-none border-border">
        <CardHeader className="pb-3">
          <CardTitle as="h2" className="text-base">Critical path</CardTitle>
          <p className="text-sm text-muted-foreground">Downstream dates inherit the state of the link above them.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {data.criticalPath.links.map((l) => (
              <div key={l.id} className="flex gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#242424] dark:text-foreground">{l.cleanName}</p>
                  <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                    {l.ownerRole ? `${l.ownerRole} · ` : ''}due {fmtDate(l.dueDate)} · {l.percentComplete}%
                  </p>
                  <ReasonList reasons={l.reasons} max={2} className="mt-1.5" />
                </div>
                <StateLabel state={l.confidence} className="shrink-0 self-start" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Decisions */}
      <Card className="flex flex-col gap-6 shadow-none border-border">
        <CardHeader className="pb-3">
          <CardTitle as="h2" className="text-base">Decisions required</CardTitle>
          <p className="text-sm text-muted-foreground">Awaiting a leadership call.</p>
        </CardHeader>
        <CardContent className="p-0">
          {data.decisionsPending.length ? (
            <div className="overflow-x-auto">
              <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_th]:text-foreground">
                <TableHeader>
                  <TableRow><TableHead>Decision</TableHead><TableHead className="w-28">Required of</TableHead><TableHead className="w-24">Deadline</TableHead><TableHead>Impact</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {data.decisionsPending.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.title}</TableCell>
                      <TableCell className="text-sm">{d.required_of_role || '—'}</TableCell>
                      <TableCell className="text-sm tabular-nums">{fmtDate(d.decision_deadline)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.business_impact || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={<Gavel className="h-8 w-8" />} title="No decisions logged">
              Record one when a workstream is waiting on a leadership call.
            </EmptyState>
          )}
        </CardContent>
      </Card>

      {/* Top risks */}
      <Card className="flex flex-col gap-6 shadow-none border-border">
        <CardHeader className="pb-3">
          <CardTitle as="h2" className="text-base">Top risks</CardTitle>
          <p className="text-sm text-muted-foreground">Ranked by the deterministic risk engine. Every level shows its reasons.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_th]:text-foreground">
              <TableHeader>
                <TableRow><TableHead className="min-w-[300px]">Item</TableHead><TableHead className="w-24">Owner</TableHead><TableHead className="w-20">Due</TableHead><TableHead className="w-24">Risk</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.topRisks.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="align-top">
                      <p className="font-medium text-[#242424] dark:text-foreground">{cleanName(r.name)}</p>
                      <p className="text-xs text-muted-foreground">{r.workstream}</p>
                      <ReasonList reasons={r.reasons} max={3} className="mt-1.5" />
                    </TableCell>
                    <TableCell className="align-top text-sm">{r.owner || '—'}</TableCell>
                    <TableCell className="align-top text-sm tabular-nums">{fmtDate(r.dueDate)}</TableCell>
                    <TableCell className="align-top"><RiskLevel level={r.level} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Department summaries */}
      <Card className="flex flex-col gap-6 shadow-none border-border">
        <CardHeader className="pb-3"><CardTitle as="h2" className="text-base">Department summaries</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_th]:text-foreground">
              <TableHeader>
                <TableRow>
                  <TableHead>Workstream</TableHead><TableHead className="w-28">Health</TableHead>
                  <TableHead className="w-24">Owner</TableHead><TableHead className="w-20">Open</TableHead>
                  <TableHead className="w-20">Overdue</TableHead><TableHead className="w-20">Blocked</TableHead>
                  <TableHead>Next milestone</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.departmentSummaries.map((d) => (
                  <TableRow key={d.name}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell><StateLabel state={d.health} /></TableCell>
                    <TableCell className="text-sm">{d.owner || '—'}</TableCell>
                    <TableCell className="text-sm tabular-nums">{d.open}</TableCell>
                    <TableCell className="text-sm tabular-nums">{d.overdue}</TableCell>
                    <TableCell className="text-sm tabular-nums">{d.blocked}</TableCell>
                    <TableCell className="text-sm">
                      {d.nextMilestone ? <>{d.nextMilestone.name} <span className="tabular-nums text-muted-foreground">· {fmtDate(d.nextMilestone.dueDate)}</span></> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
